import React, { useState, useEffect } from 'react';
import { 
  Users, 
  UserCheck, 
  UserX, 
  DollarSign, 
  CreditCard, 
  TrendingUp, 
  Clock,
  Wallet,
  Activity,
  Target,
  Briefcase,
  BarChart,
  Award,
  RefreshCw,
  Package,
  Layers,
  Coins,
  ArrowDownRight,
  Eye,
  Shield,
  Database,
  AlertCircle,
  Zap,
  Battery,
  Calculator,
  TrendingDown,
  AlertTriangle,
  ArrowUpRight,
  BarChart2
} from 'lucide-react';
import { database, ref, get } from '../../firebase';

const DashboardOverview = ({ onNavigate }) => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    adminUsers: 0,
    normalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    adminBalance: 0,
    totalWalletBalance: 0,
    totalFundBalance: 0,
    totalWorkingBalance: 0,
    totalEnergyBalance: 0,
    totalRoiWalletBalance: 0,
    totalFundAdded: 0,
    totalFundDeducted: 0,
    totalFundTransactions: 0,
    totalPackages: 0,
    activePackages: 0,
    todayTotalROI: 0,
    totalLevelIncome: 0,
    monthBusiness: 0,
    totalBusiness: 0,
    totalWithdrawals: 0,
    pendingWithdrawals: 0,
    totalPendingAmount: 0,
    totalPaidAmount: 0,
    totalReferrals: 0,
    levelIncomeBreakdown: {
      level1: 0,
      level2: 0,
      level3: 0,
      level4: 0,
      level5: 0
    }
  });

  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatCurrency = (amount) => {
    const value = Number(amount) || 0;
    if (value >= 1000000) {
      return '$' + (value / 1000000).toFixed(1) + 'M';
    }
    if (value >= 1000) {
      return '$' + (value / 1000).toFixed(1) + 'K';
    }
    return '$' + value.toFixed(2);
  };

  const calculateProgress = (value, maxValue) => {
    if (!maxValue || maxValue === 0) return 0;
    return Math.min((value / maxValue) * 100, 100);
  };

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Fetch all users from date-wise structure
      const usersRef = ref(database, 'users');
      const usersSnapshot = await get(usersRef);
      
      let totalUsers = 0;
      let adminUsers = 0;
      let normalUsers = 0;
      let activeUsers = 0;
      let inactiveUsers = 0;
      let totalWalletBalance = 0;
      let totalFundBalance = 0;
      let totalWorkingBalance = 0;
      let totalEnergyBalance = 0;
      let totalRoiWalletBalance = 0;
      let activePackages = 0;
      let monthBusiness = 0;
      let totalBusiness = 0;
      let totalReferrals = 0;
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const today = new Date().toISOString().split('T')[0];

      if (usersSnapshot.exists()) {
        const usersByDate = usersSnapshot.val();
        
        // Process all users from all date folders
        Object.entries(usersByDate).forEach(([date, users]) => {
          Object.entries(users).forEach(([dbKey, user]) => {
            if (!user) return;
            
            // Skip admin users for normal user count
            if (user.role === 'admin') {
              adminUsers++;
              return;
            }
            
            totalUsers++;
            normalUsers++;
            
            // Calculate wallet balances
            const aurumWallet = parseFloat(user.wallets?.aurum) || 0;
            totalWalletBalance += aurumWallet;
            
            const workingWallet = parseFloat(user.wallets?.working) || 0;
            totalWorkingBalance += workingWallet;
            
            const energyWallet = parseFloat(user.wallets?.energy) || 0;
            totalEnergyBalance += energyWallet;
            
            const roiWallet =
              parseFloat(user.wallets?.roi) ||
              parseFloat(user.wallets?.roiWallet) ||
              parseFloat(user.wallets?.roiwallet) ||
              parseFloat(user.roiWallet) ||
              parseFloat(user.roiwallet) ||
              parseFloat(user.wallets?.roiIncome) ||
              parseFloat(user.roiIncome) ||
              0;
            totalRoiWalletBalance += roiWallet;
            
            // Calculate fund balance
            let userFundBalance = 0;
            if (user.fund) {
              if (typeof user.fund === 'object') {
                userFundBalance = parseFloat(user.fund.balance || 0);
              } else {
                userFundBalance = parseFloat(user.fund) || 0;
              }
            }
            totalFundBalance += userFundBalance;
            
            // Check if user is active (has active packages)
            let hasActivePackage = false;
            let userActivePackages = 0;
            let userTotalBusiness = 0;
            let userMonthBusiness = 0;
            
            if (user.packages && Array.isArray(user.packages)) {
              user.packages.forEach(pkg => {
                if (pkg.status === 'active') {
                  hasActivePackage = true;
                  userActivePackages++;
                  activePackages++;
                  
                  const amount = parseFloat(pkg.totalAmount || pkg.investmentAmount || 0);
                  userTotalBusiness += amount;
                  totalBusiness += amount;
                  
                  // Check if package was purchased this month
                  if (pkg.purchaseDate) {
                    try {
                      const purchaseDate = new Date(pkg.purchaseDate);
                      if (purchaseDate.getMonth() === currentMonth && 
                          purchaseDate.getFullYear() === currentYear) {
                        userMonthBusiness += amount;
                        monthBusiness += amount;
                      }
                    } catch (e) {
                      console.log('Invalid date format for package:', e);
                    }
                  }
                }
              });
            }
            
            if (hasActivePackage) {
              activeUsers++;
            } else {
              inactiveUsers++;
            }
            
            // Count referrals
            if (user.referrals && Array.isArray(user.referrals)) {
              totalReferrals += user.referrals.length;
            }
          });
        });
      }
      
      // Fetch packages count
      let totalPackages = 0;
      const packagesRef = ref(database, 'Packages');
      const packagesSnapshot = await get(packagesRef);
      if (packagesSnapshot.exists()) {
        totalPackages = Object.keys(packagesSnapshot.val()).length;
      }
      
      // Fetch ROI data for today
      let todayTotalROI = 0;
      let totalLevelIncome = 0;
      let totalActiveDailyROI = 0;
      const levelIncomeBreakdown = { level1: 0, level2: 0, level3: 0, level4: 0, level5: 0 };
      
      // Check ROI schedule for ROI values
      const roiScheduleRef = ref(database, 'ROISchedule');
      const roiScheduleSnapshot = await get(roiScheduleRef);
      if (roiScheduleSnapshot.exists()) {
        const roiData = roiScheduleSnapshot.val();
        Object.entries(roiData).forEach(([date, users]) => {
          Object.entries(users || {}).forEach(([userId, schedules]) => {
            Object.entries(schedules || {}).forEach(([scheduleId, schedule]) => {
                let roiStatus = schedule?.roiStatus || 'active';
                if (
                  schedule?.blockStatus?.isROIBlocked ||
                  schedule?.blockStatus?.userROIBlocked ||
                  schedule?.blockStatus?.packageROIBlocked ||
                  schedule?.blockStatus?.roiScheduleBlocked
                ) {
                  roiStatus = 'blocked';
                }
                if (roiStatus === 'active') {
                  const dailyROIAmount =
                    parseFloat(schedule?.dailyROIAmount) ||
                    ((parseFloat(schedule?.investmentAmount) || 0) * (parseFloat(schedule?.dailyROI) || 0)) / 100;

                  totalActiveDailyROI += dailyROIAmount;
                  const dueDate = schedule?.nextRoiDateKey || date;
                  if (dueDate === today) {
                  todayTotalROI += dailyROIAmount;
                  }
                }                
              });
            });
        });
      }
      
      // Check level incomes (all dates)
      const levelIncomesRef = ref(database, 'levelIncomes');
      const levelIncomesSnapshot = await get(levelIncomesRef);
      if (levelIncomesSnapshot.exists()) {
        const levelData = levelIncomesSnapshot.val();
        Object.entries(levelData).forEach(([date, users]) => {
          Object.entries(users).forEach(([userId, incomes]) => {
            Object.entries(incomes).forEach(([incomeId, income]) => {
              if (income.status !== 'blocked') {
                const amount = parseFloat(income.amount) || 0;
                totalLevelIncome += amount;
                
                const level = parseInt(income.level) || 1;
                if (level >= 1 && level <= 5) {
                  levelIncomeBreakdown[`level${level}`] += amount;
                }
              }
            });
          });
        });
      }

      // Fallback ROI total from credited ROI incomes
      let totalRoiIncomeFromHistory = 0;
      const roiIncomesRef = ref(database, 'roiIncomes');
      const roiIncomesSnapshot = await get(roiIncomesRef);
      if (roiIncomesSnapshot.exists()) {
        const roiIncomesData = roiIncomesSnapshot.val();
        Object.values(roiIncomesData || {}).forEach((users) => {
          Object.values(users || {}).forEach((incomes) => {
            Object.values(incomes || {}).forEach((income) => {
              const status = String(income?.status || '').toLowerCase();
              if (status === 'blocked') return;
              const amount =
                parseFloat(income?.amount) ||
                parseFloat(income?.roiAmount) ||
                parseFloat(income?.dailyROIAmount) ||
                0;
              totalRoiIncomeFromHistory += amount;
            });
          });
        });
      }
      
      // Fetch withdrawal data
      let totalWithdrawals = 0;
      let pendingWithdrawals = 0;
      let totalPendingAmount = 0;
      let totalPaidAmount = 0;
      
      const withdrawalsRef = ref(database, 'withdrawal');
      const withdrawalsSnapshot = await get(withdrawalsRef);
      if (withdrawalsSnapshot.exists()) {
        const withdrawalData = withdrawalsSnapshot.val();
        Object.entries(withdrawalData).forEach(([date, withdrawals]) => {
          Object.entries(withdrawals).forEach(([withdrawalId, withdrawal]) => {
            if (withdrawal) {
              totalWithdrawals++;
              const amount = parseFloat(withdrawal.amount) || 0;
              const status = String(withdrawal.status || '').toLowerCase();
              
              if (status === 'pending') {
                pendingWithdrawals++;
                totalPendingAmount += amount;
              } else if (status === 'approved' || status === 'completed' || status === 'paid') {
                totalPaidAmount += amount;
              }
            }
          });
        });
      }
      
      // Fetch fund transactions
      let totalFundAdded = 0;
      let totalFundDeducted = 0;
      let totalFundTransactions = 0;
      
      try {
        const fundManagementRef = ref(database, 'FundManagement');
        const fundManagementSnapshot = await get(fundManagementRef);
        if (fundManagementSnapshot.exists()) {
          const fundData = fundManagementSnapshot.val();
          
          // Process AddedFundByAdmin
          if (fundData.AddedFundByAdmin) {
            Object.entries(fundData.AddedFundByAdmin).forEach(([userId, transactions]) => {
              if (transactions) {
                Object.values(transactions).forEach(transaction => {
                  if (transaction && transaction.type === 'Add') {
                    totalFundTransactions++;
                    totalFundAdded += parseFloat(transaction.amount) || 0;
                  }
                });
              }
            });
          }
          
          // Process DeductedFundByAdmin
          if (fundData.DeductedFundByAdmin) {
            Object.entries(fundData.DeductedFundByAdmin).forEach(([userId, transactions]) => {
              if (transactions) {
                Object.values(transactions).forEach(transaction => {
                  if (transaction && transaction.type === 'Deduct') {
                    totalFundTransactions++;
                    totalFundDeducted += parseFloat(transaction.amount) || 0;
                  }
                });
              }
            });
          }
        }
      } catch (error) {
        console.error('Error fetching fund transactions:', error);
      }
      
      const resolvedRoiWalletTotal =
        totalRoiWalletBalance > 0
          ? totalRoiWalletBalance
          : (totalRoiIncomeFromHistory > 0 ? totalRoiIncomeFromHistory : totalActiveDailyROI);

      setStats({
        totalUsers: adminUsers + normalUsers,
        adminUsers,
        normalUsers,
        activeUsers,
        inactiveUsers,
        adminBalance: 0, // You can add admin balance calculation if needed
        totalWalletBalance,
        totalFundBalance,
        totalWorkingBalance,
        totalEnergyBalance,
        totalRoiWalletBalance: resolvedRoiWalletTotal,
        totalFundAdded,
        totalFundDeducted,
        totalFundTransactions,
        totalPackages,
        activePackages,
        todayTotalROI,
        totalLevelIncome,
        monthBusiness,
        totalBusiness,
        totalWithdrawals,
        pendingWithdrawals,
        totalPendingAmount,
        totalPaidAmount,
        totalReferrals,
        levelIncomeBreakdown
      });
      
      setLastUpdated(new Date());
      setLoading(false);
      
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setLoading(false);
    }
  };

  const handleCardClick = (type) => {
    return;
  };

  const totalSystemBalance = stats.totalWalletBalance + stats.totalFundBalance + 
                            stats.totalWorkingBalance + stats.totalEnergyBalance + 
                            stats.totalRoiWalletBalance;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <div className="text-white font-medium">Loading dashboard data...</div>
          <div className="text-gray-400 text-sm mt-2">Fetching real-time statistics</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
      {/* Header - Mobile Responsive */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Admin Dashboard</h1>
            <p className="text-gray-400 text-sm sm:text-base">Real-time statistics and insights</p>
            {lastUpdated && (
              <p className="text-gray-500 text-xs sm:text-sm mt-1">
                Last updated: {lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </p>
            )}
          </div>
          <div className="flex justify-end">
            <button 
              onClick={fetchDashboardStats}
              className="btn-electric flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium w-full sm:w-auto"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Refresh Data</span>
              <span className="sm:hidden">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Grid - 2 columns on mobile, 4 on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        {/* Total Users */}
        <div 
          onClick={() => handleCardClick('users')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-primary/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <div className="text-gray-400 text-xs sm:text-sm mb-1">Total Users</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatNumber(stats.totalUsers)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <Users className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-primary" />
            </div>
          </div>
          <div className="text-primary text-xs sm:text-sm mb-2">
            Normal: {stats.normalUsers} 
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-primary h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.normalUsers, Math.max(stats.totalUsers, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>Normal Users</span>
            <span>{Math.round(calculateProgress(stats.normalUsers, Math.max(stats.totalUsers, 1)))}%</span>
          </div>
        </div>

        {/* Active Users */}
        <div 
          onClick={() => handleCardClick('activeUsers')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-green-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <div className="text-green-400 text-xs sm:text-sm mb-1">Active Users</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatNumber(stats.activeUsers)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
              <UserCheck className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-green-400" />
            </div>
          </div>
          <div className="text-green-400 text-xs sm:text-sm mb-2">
            Rate: {Math.round((stats.activeUsers / Math.max(stats.normalUsers, 1)) * 100) || 0}%
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-green-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.activeUsers, Math.max(stats.normalUsers, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>Active Rate</span>
            <span>{Math.round(calculateProgress(stats.activeUsers, Math.max(stats.normalUsers, 1)))}%</span>
          </div>
        </div>

        {/* Inactive Users */}
        <div 
          onClick={() => handleCardClick('inactiveUsers')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-red-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <div className="text-red-400 text-xs sm:text-sm mb-1">Inactive Users</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatNumber(stats.inactiveUsers)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-red-500/10 flex items-center justify-center group-hover:bg-red-500/20 transition-colors">
              <UserX className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-red-400" />
            </div>
          </div>
          <div className="text-red-400 text-xs sm:text-sm mb-2">
            No active packages
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-red-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.inactiveUsers, Math.max(stats.normalUsers, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>Inactive Rate</span>
            <span>{Math.round(calculateProgress(stats.inactiveUsers, Math.max(stats.normalUsers, 1)))}%</span>
          </div>
        </div>

        {/* Total Referrals */}
        <div 
          onClick={() => handleCardClick('referrals')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-pink-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <div className="text-pink-400 text-xs sm:text-sm mb-1">Total Referrals</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatNumber(stats.totalReferrals)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-pink-500/10 flex items-center justify-center group-hover:bg-pink-500/20 transition-colors">
              <ArrowDownRight className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-pink-400" />
            </div>
          </div>
          <div className="text-pink-400 text-xs sm:text-sm mb-2">
            Per user: {Math.round(stats.totalReferrals / Math.max(stats.normalUsers, 1))}
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-pink-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.totalReferrals, Math.max(stats.normalUsers * 10, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>Referral Rate</span>
            <span>{Math.round(calculateProgress(stats.totalReferrals, Math.max(stats.normalUsers * 10, 1)))}%</span>
          </div>
        </div>
      </div>

      {/* Business Row - Mobile Responsive */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        {/* Total Business */}
        <div 
          onClick={() => handleCardClick('business')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-primary/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <div className="text-primary text-xs sm:text-sm mb-1">Total Business</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatCurrency(stats.totalBusiness)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <Award className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-primary" />
            </div>
          </div>
          <div className="text-primary text-xs sm:text-sm mb-2">
            This Month: {formatCurrency(stats.monthBusiness)}
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-primary h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.monthBusiness, Math.max(stats.totalBusiness, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>Month Share</span>
            <span>{Math.round(calculateProgress(stats.monthBusiness, Math.max(stats.totalBusiness, 1)))}%</span>
          </div>
        </div>

        {/* Total Level Income */}
        <div 
          onClick={() => handleCardClick('levelIncome')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-blue-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <div className="text-blue-400 text-xs sm:text-sm mb-1">Total Level Income</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatCurrency(stats.totalLevelIncome)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
              <Target className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-blue-400" />
            </div>
          </div>
          <div className="text-blue-400 text-xs sm:text-sm mb-2">
            Level commissions
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-blue-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.totalLevelIncome, Math.max(stats.todayTotalROI + stats.totalLevelIncome, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>Level Share</span>
            <span>{Math.round(calculateProgress(stats.totalLevelIncome, Math.max(stats.todayTotalROI + stats.totalLevelIncome, 1)))}%</span>
          </div>
        </div>

        {/* Pending Withdrawals */}
        <div 
          onClick={() => handleCardClick('withdrawals')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-yellow-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <div className="text-yellow-400 text-xs sm:text-sm mb-1">Pending Withdrawals</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatCurrency(stats.totalPendingAmount)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-yellow-500/10 flex items-center justify-center group-hover:bg-yellow-500/20 transition-colors">
              <Clock className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-yellow-400" />
            </div>
          </div>
          <div className="text-yellow-400 text-xs sm:text-sm mb-2">
            {formatNumber(stats.pendingWithdrawals)} requests
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-yellow-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.pendingWithdrawals, Math.max(stats.totalWithdrawals, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>Pending Rate</span>
            <span>{Math.round(calculateProgress(stats.pendingWithdrawals, Math.max(stats.totalWithdrawals, 1)))}%</span>
          </div>
        </div>

        {/* Total Packages */}
        <div 
          onClick={() => handleCardClick('packages')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-purple-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <div className="text-purple-400 text-xs sm:text-sm mb-1">Total Packages</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatNumber(stats.totalPackages)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
              <Layers className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-purple-400" />
            </div>
          </div>
          <div className="text-purple-400 text-xs sm:text-sm mb-2">
            Master packages
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-purple-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.totalPackages, Math.max(stats.totalPackages + stats.activePackages, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>Master Packages</span>
            <span>{Math.round(calculateProgress(stats.totalPackages, Math.max(stats.totalPackages + stats.activePackages, 1)))}%</span>
          </div>
        </div>
      </div>

      {/* Wallet Balances Row - Mobile Responsive (2 columns on mobile, 3 on tablet, 5 on desktop) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        {/* Total System Balance */}
        <div 
          onClick={() => handleCardClick('walletReport')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-emerald-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1">
              <div className="text-emerald-400 text-xs sm:text-sm mb-1">Total Balance</div>
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-white">{formatCurrency(totalSystemBalance)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-500/10 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
              <Wallet className="h-4 w-4 sm:h-5 sm:w-5 text-emerald-400" />
            </div>
          </div>
          <div className="text-emerald-400 text-xs mb-2">
            All wallets
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2">
            <div 
              className="bg-emerald-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(totalSystemBalance, Math.max(stats.totalBusiness * 2, 1))}%` }}
            ></div>
          </div>
        </div>

        {/* Aerovolt Wallet */}
        <div 
          onClick={() => handleCardClick('walletReport')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-primary/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1">
              <div className="text-primary text-xs sm:text-sm mb-1">Aerovolt Wallet</div>
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-white">{formatCurrency(stats.totalWalletBalance)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
          </div>
          <div className="text-primary text-xs mb-2">
            Main wallet
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2">
            <div 
              className="bg-primary h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.totalWalletBalance, Math.max(totalSystemBalance, 1))}%` }}
            ></div>
          </div>
        </div>

        {/* AV PAY Wallet */}
        <div 
          onClick={() => handleCardClick('walletReport')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-purple-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1">
              <div className="text-purple-400 text-xs sm:text-sm mb-1">AV PAY Wallet</div>
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-white">{formatCurrency(stats.totalFundBalance)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
              <Database className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400" />
            </div>
          </div>
          <div className="text-purple-400 text-xs mb-2">
            Fund wallet
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2">
            <div 
              className="bg-purple-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.totalFundBalance, Math.max(totalSystemBalance, 1))}%` }}
            ></div>
          </div>
        </div>

        {/* AV Power Wallet */}
        <div 
          onClick={() => handleCardClick('walletReport')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-yellow-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1">
              <div className="text-yellow-400 text-xs sm:text-sm mb-1">AV Power Wallet</div>
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-white">{formatCurrency(stats.totalWorkingBalance)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-yellow-500/10 flex items-center justify-center group-hover:bg-yellow-500/20 transition-colors">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
            </div>
          </div>
          <div className="text-yellow-400 text-xs mb-2">
            Working capital
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2">
            <div 
              className="bg-yellow-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.totalWorkingBalance, Math.max(totalSystemBalance, 1))}%` }}
            ></div>
          </div>
        </div>

        {/* AV Energy Wallet */}
        <div 
          onClick={() => handleCardClick('walletReport')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-cyan-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex-1">
              <div className="text-cyan-400 text-xs sm:text-sm mb-1">AV Energy Wallet</div>
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-white">{formatCurrency(stats.totalEnergyBalance)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-cyan-500/10 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
              <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-400" />
            </div>
          </div>
          <div className="text-cyan-400 text-xs mb-2">
            Energy reserves
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2">
            <div 
              className="bg-cyan-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.totalEnergyBalance, Math.max(totalSystemBalance, 1))}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Additional Stats Row - Mobile Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        {/* AV ROI Wallet */}
        <div 
          className="card-glow p-4 sm:p-6 group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-orange-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex-1">
              <div className="text-orange-400 text-xs sm:text-sm mb-1">AV ROI Wallet</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatCurrency(stats.totalRoiWalletBalance)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-orange-500/10 flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
              <Calculator className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-orange-400" />
            </div>
          </div>
          <div className="text-orange-400 text-xs sm:text-sm mb-2">
            ROI accumulations
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-orange-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.totalRoiWalletBalance, Math.max(totalSystemBalance, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>System Share</span>
            <span>{Math.round(calculateProgress(stats.totalRoiWalletBalance, Math.max(totalSystemBalance, 1)))}%</span>
          </div>
        </div>

        {/* Active Packages */}
        <div 
          onClick={() => handleCardClick('packages')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-teal-500/50 transition-all"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <div className="text-teal-400 text-xs sm:text-sm mb-1">Active Packages</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatNumber(stats.activePackages)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-teal-500/10 flex items-center justify-center group-hover:bg-teal-500/20 transition-colors">
              <Briefcase className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-teal-400" />
            </div>
          </div>
          <div className="text-teal-400 text-xs sm:text-sm mb-2">
            Active user packages
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-teal-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.activePackages, Math.max(stats.activePackages + stats.totalPackages, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>Active Usage</span>
            <span>{Math.round(calculateProgress(stats.activePackages, Math.max(stats.activePackages + stats.totalPackages, 1)))}%</span>
          </div>
        </div>

        {/* Total Withdrawals */}
        <div 
          onClick={() => handleCardClick('withdrawals')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-orange-500/50 transition-all col-span-1 sm:col-span-1"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <div className="text-orange-400 text-xs sm:text-sm mb-1">Total Withdrawals</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatNumber(stats.totalWithdrawals)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-orange-500/10 flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
              <Coins className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-orange-400" />
            </div>
          </div>
          <div className="text-orange-400 text-xs sm:text-sm mb-2">
            Total: {formatCurrency(stats.totalPaidAmount + stats.totalPendingAmount)}
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-orange-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.totalWithdrawals, Math.max(stats.activeUsers, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>Per Active User</span>
            <span>{Math.round(calculateProgress(stats.totalWithdrawals, Math.max(stats.activeUsers, 1)))}%</span>
          </div>
        </div>

        {/* Admin Balance */}
        {/* <div 
          onClick={() => handleCardClick('funds')}
          className="card-glow p-4 sm:p-6 cursor-pointer group relative bg-gray-800/50 rounded-xl border border-gray-700/50 hover:border-blue-500/50 transition-all col-span-1 sm:col-span-1"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div>
              <div className="text-blue-400 text-xs sm:text-sm mb-1">Admin Balance</div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{formatCurrency(stats.adminBalance)}</div>
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
              <BarChart2 className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-blue-400" />
            </div>
          </div>
          <div className="text-blue-400 text-xs sm:text-sm mb-2">
            System admin funds
          </div>
          <div className="w-full bg-gray-700 rounded-full h-1.5 sm:h-2 mb-1 sm:mb-2">
            <div 
              className="bg-blue-500 h-1.5 sm:h-2 rounded-full transition-all duration-500"
              style={{ width: `${calculateProgress(stats.adminBalance, Math.max(totalSystemBalance, 1))}%` }}
            ></div>
          </div>
          <div className="text-gray-500 text-xs flex justify-between">
            <span>System Share</span>
            <span>{Math.round(calculateProgress(stats.adminBalance, Math.max(totalSystemBalance, 1)))}%</span>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default DashboardOverview;

